import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import { useSelector, useDispatch } from "react-redux";
import MUIDataTable from "mui-datatables";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// MUI imports
import {
  Button,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Typography,
} from "@mui/material";

// Project Imports
import ApprovalGroup from "./UI/ApprovalGroup";
import TextFieldTR from "./UI/TextFieldTR";
import { components } from "../../Components/Icons/DataTableIcons";

import { generateCourseTableData } from "./helpers";

import {
  paNextFormStep,
  physicallyApproveTrainer,
  physicallyApproveTrainerReset,
} from "../../Actions";

const ColumnTextStyle = {
  textTransform: "capitalize",
  textAlign: "left",
  color: "#212b36",
  fontSize: "0.875rem",
  fontWeight: 600,
};

// Custom Components
const CustomTableColumnHeaders = ({ index, column }) => {
  const headStyle = {};

  if (column.label === "First Name") {
    headStyle.paddingLeft = "24px";
  }
  return (
    <Typography style={headStyle} sx={ColumnTextStyle} key={index}>
      {column.label}
    </Typography>
  );
};

const CustomDatatableTitle = ({ title }) => (
  <Box
    display="flex"
    alignItems="center"
    gap={10}
    justifyContent="space-between"
  >
    <Typography sx={{ color: "#05004E", fontWeight: 600 }}>{title}</Typography>
  </Box>
);

const AdminActionButton = ({ type, children, setType, ...otherProps }) => {
  const { submitForm } = useFormikContext();

  const handleSubmit = () => {
    setType(type);
    submitForm(type);
  };

  const configButton = {
    variant: "contained",
    color: "primary",
    onClick: handleSubmit,
    ...otherProps,
  };

  return <Button {...configButton}>{children}</Button>;
};

const paColumnOptions = {
  customHeadLabelRender: ({ index, ...column }) => {
    return <CustomTableColumnHeaders index={index} column={column} />;
  },
};

const coursesTableOptions = {
  filter: false,
  // count: tablePaginationData.totalCount,
  disableRipple: true,
  elevation: 0,
  fixedHeader: true,
  print: false,
  responsive: "standard",
  rowsPerPage: 10,
  rowsPerPageOptions: [10],
  search: false,
  selectableRows: "none",
  serverSide: true,
};

const formValidationSchema = Yup.object().shape({
  isSkillCenterCourseContents: Yup.boolean().required(
    "This is a required field"
  ),
  adminCommentOnImpact: Yup.string().required("This is a required field"),
  adminCommentOnTrainingCurriculum: Yup.string().required(
    "This is a required field"
  ),
  adminFindingsSummary: Yup.string().required("This is a required field"),
});

const formState = {
  isSkillCenterCourseContents: null,
  adminCommentOnImpact: "",
  adminCommentOnTrainingCurriculum: "",
  adminFindingsSummary: "",
};

const sectionSixMarkup = [
  {
    name: "adminCommentOnImpact",
    label:
      "1. In your assessment, is the training centre capable of delivering rich training to Corps member to the extent of starting a business upon completion of the training? If yes, state justification. If No, state your reason.",
  },
  {
    name: "adminCommentOnTrainingCurriculum",
    label:
      "2. Does the centre have a training curriculum for the skill area(s) listed above?",
  },
];

const PartThree = () => {
  const navigate = useNavigate();
  const globalDispatch = useDispatch();
  const tregPhysicalApprovalReducer = (globalState) =>
    globalState.tregPhysicalApprovalReducer;

  const {
    singleTrainerDetails,
    physicallyApprovingTrainer,
    physicalApprovalSuccess,
    approvalResults,
  } = useSelector(tregPhysicalApprovalReducer);

  const [courseTableData, setCourseTableData] = useState([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [localApprovalResults, setLocalApprovalResults] = useState({});
  const [actionType, setActionType] = useState("");

  useEffect(() => {
    if (Object.keys(singleTrainerDetails).length > 0) {
      const courseData = generateCourseTableData(singleTrainerDetails);
      setCourseTableData(courseData);
    }
  }, [singleTrainerDetails]);

  useEffect(() => {
    if (
      // if approval request is 200 OK, redirect
      physicalApprovalSuccess === true &&
      physicallyApprovingTrainer === false
    ) {
      navigate("/home/trainersmanagement/physicalApproval");
      globalDispatch(physicallyApproveTrainerReset());
    }
  }, [
    globalDispatch,
    navigate,
    physicalApprovalSuccess,
    physicallyApprovingTrainer,
  ]);

  const courseColumns = [
    {
      name: "skill",
      label: "Skill Area",
      options: {
        ...paColumnOptions,
        setCellProps: () => ({
          style: { paddingLeft: "24px" },
        }),
      },
    },
    {
      name: "courseDuration",
      label: "Course Duration",
      options: paColumnOptions,
    },
    {
      name: "hoursPerDay",
      label: "Hours per Day",
      options: paColumnOptions,
    },
  ];

  function handleFormSubmit(values) {
    setLocalApprovalResults({
      ...values,
    });
    setConfirmDialogOpen(true);
  }

  function handleCloseConfirmDialog() {
    setConfirmDialogOpen(false);
  }

  function handleSubmitApproval() {
    const requestBody = {
      ...localApprovalResults,
      ...approvalResults,
      isApprove: true,
      commentOnRejection: "",
    };

    globalDispatch(physicallyApproveTrainer(requestBody));
  }

  function handleSubmitDisapproval(values) {
    // fire api call here
    const requestBody = {
      ...localApprovalResults,
      ...approvalResults,
      ...values,
      isApprove: false,
    };

    globalDispatch(physicallyApproveTrainer(requestBody));
  }

  return (
    <>
      <Formik
        initialValues={{ ...formState }}
        validationSchema={formValidationSchema}
        onSubmit={handleFormSubmit}
      >
        <Form>
          <>
            <h2>Section 5</h2>

            {/*  Course Assessments */}
            <div>
              <MUIDataTable
                title={<CustomDatatableTitle title={"Course Assessments"} />}
                columns={courseColumns}
                components={components}
                options={coursesTableOptions}
                data={courseTableData}
              />
              <ApprovalGroup fieldName={"isSkillCenterCourseContents"} />
            </div>

            <h2>Section 6</h2>

            {sectionSixMarkup.map((field) => (
              <TextFieldTR
                name={field.name}
                label={field.label}
                key={field.label}
              />
            ))}
          </>
          <>
            <h2>Section 7</h2>

            <TextFieldTR
              name={"adminFindingsSummary"}
              label={"Kindly summarise your findings on the training centre"}
              key={"officerFindingsSummary"}
            />
          </>

          <AdminActionButton
            setType={setActionType}
            type={"disapprove"}
            children={"Disapprove"}
            color="warning"
            variant="outlined"
          />
          <AdminActionButton
            setType={setActionType}
            type={"approve"}
            children={"Approve"}
          />
        </Form>
      </Formik>

      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        {actionType === "disapprove" && (
          <Formik
            onSubmit={handleSubmitDisapproval}
            initialValues={{
              commentOnRejection: "",
            }}
            validationSchema={Yup.object().shape({
              commentOnRejection: Yup.string().required(
                "This is a required field"
              ),
            })}
          >
            <Form>
              <DialogContent>
                <TextFieldTR
                  name={"commentOnRejection"}
                  label={"Rejection Comment*"}
                />
              </DialogContent>
              <DialogActions>
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={handleCloseConfirmDialog}
                >
                  Cancel
                </Button>
                <Button variant="contained" type="submit" autoFocus>
                  {physicallyApprovingTrainer === true ? (
                    <>
                      <CircularProgress
                        size={12}
                        sx={{ backgroundColor: "white" }}
                      />
                      Processing
                    </>
                  ) : (
                    <>Disapprove </>
                  )}
                </Button>
              </DialogActions>
            </Form>
          </Formik>
        )}

        {actionType === "approve" && (
          <>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                Are you sure you want to approve this training center?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                variant="outlined"
                color="warning"
                onClick={handleCloseConfirmDialog}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmitApproval}
                autoFocus
                disabled={physicallyApprovingTrainer === true}
              >
                {physicallyApprovingTrainer === true ? (
                  <>
                    <CircularProgress
                      size={12}
                      sx={{ backgroundColor: "white" }}
                    />
                    Processing
                  </>
                ) : (
                  <>Approve </>
                )}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default PartThree;
